import { useState } from "react";
import {
  resideInvestmentSchema,
  RESIDE_TYPES, RESIDE_STRATEGIES, RESIDE_PURPOSES, RESIDE_READINESS, RESIDE_BUDGETS, RESIDE_TIMELINES, RESIDE_CHANNELS,
  residePropertyTypeLabels, resideStrategyLabels, residePurposeLabels, resideReadinessLabels, resideBudgetLabels, resideTimelineLabels, resideChannelLabels,
  type ResideInvestmentPayload,
} from "@anton/shared";
import { PageHeader } from "../components/PageHeader";
import { Field } from "../components/Field";
import { RadioGroup } from "../components/RadioGroup";
import { SubmitButton } from "../components/SubmitButton";
import { SuccessScreen } from "../components/SuccessScreen";
import { Card, Lead, P, H3, CheckList, Stat, SectionHeader } from "../components/Section";
import { submitApplication } from "../lib/api";
import { haptic, showAlert } from "../lib/telegram";

export default function ResideInvestment() {
  const [form, setForm] = useState<Partial<ResideInvestmentPayload>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (done) return <SuccessScreen message="Спасибо! Аналитик RESIDE свяжется в ближайшее время и пришлёт расчёт под твой запрос." />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = resideInvestmentSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const i of parsed.error.issues) fe[i.path.join(".")] = i.message;
      setErrors(fe);
      haptic("error");
      return;
    }
    setLoading(true);
    const res = await submitApplication("RESIDE_INVESTMENT", parsed.data);
    setLoading(false);
    if (!res.ok) { showAlert(res.error ?? "Ошибка"); haptic("error"); return; }
    setDone(true);
  }

  return (
    <div className="pb-10">
      <PageHeader
        eyebrow="RESIDE"
        title="Инвестиции в недвижимость на Бали"
        subtitle="Премиальный сервис пассивного дохода: от анализа объекта до выплаты дивидендов инвестору."
        icon="💰"
        iconColor="teal"
      />
      <div className="px-5 mt-3 space-y-3">
        <Card>
          <Lead>
            Бали — 4 место в мире по доходности недвижимости по версии Forbes.
            Я основатель группы компаний RESIDE: бюро + инвест-клуб + управляющая компания.
          </Lead>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Stat value="15–20%" label="доходность/год" />
          <Stat value="0%" label="комиссия агентства" />
          <Stat value="25%" label="первый взнос" />
          <Stat value="0%" label="рассрочка" />
        </div>

        <Card>
          <H3>4 стратегии инвестирования</H3>
          <div className="space-y-2 mt-2">
            <div className="ios-callout-muted">
              <b>Консервативная — 10–12%/год</b>. Готовый объект, доход с аренды. От $150k. Окупаемость 8–10 лет.
            </div>
            <div className="ios-callout-muted">
              <b>Сбалансированная — 15–22%/год</b>. Покупка на старте, продажа на сдаче. От $100k. Окупаемость 4–7 лет.
            </div>
            <div className="ios-callout-muted">
              <b>Высокодоходная — 18–25%/год</b>. Старт строительства + 5 лет аренды + перепродажа. От $100k.
            </div>
            <div className="ios-callout-muted">
              <b>Off-plan — &gt; 25%/год</b>. Цена сильно ниже розничной на старте продаж. От $80k.
            </div>
          </div>
        </Card>

        <Card>
          <H3>Регионы</H3>
          <P>
            Чангу (апартаменты от $106k, доход от $15k/год) ·
            Улувату (виллы от $388k, доход от $31k/год) ·
            Убуд (таунхаусы от $102k, доход от $18k/год).
          </P>
        </Card>

        <Card>
          <H3>Что включено в работу RESIDE</H3>
          <CheckList items={[
            "Подбор объекта под твою стратегию",
            "Due Diligence + полное юридическое сопровождение сделки",
            "Управление недвижимостью: аренда, отчётность, налоги, клининг, ремонт, охрана 24/7",
            "Постпродажный клиентский сервис",
          ]} />
        </Card>

        <div className="ios-callout">
          🔥 Ответь на 7 вопросов ниже — пришлём расчёт ожидаемой доходности под твой запрос.
        </div>
      </div>

      <SectionHeader>Анкета — 7 шагов</SectionHeader>
      <form noValidate onSubmit={onSubmit} className="px-5 space-y-4">
        <Field label="1. Тип недвижимости" error={errors["propertyType"]}>
          <RadioGroup value={form.propertyType} onChange={(v) => setForm({ ...form, propertyType: v as any })}
            options={RESIDE_TYPES.map((v) => ({ value: v, label: residePropertyTypeLabels[v] }))} />
        </Field>
        <Field label="2. Стратегия инвестирования" error={errors["strategy"]}>
          <RadioGroup value={form.strategy} onChange={(v) => setForm({ ...form, strategy: v as any })}
            options={RESIDE_STRATEGIES.map((v) => ({ value: v, label: resideStrategyLabels[v] }))} />
        </Field>
        <Field label="3. Цель приобретения" error={errors["purpose"]}>
          <RadioGroup value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v as any })}
            options={RESIDE_PURPOSES.map((v) => ({ value: v, label: residePurposeLabels[v] }))} />
        </Field>
        <Field label="4. Готовая или строящаяся" error={errors["readiness"]}>
          <RadioGroup value={form.readiness} onChange={(v) => setForm({ ...form, readiness: v as any })}
            options={RESIDE_READINESS.map((v) => ({ value: v, label: resideReadinessLabels[v] }))} />
        </Field>
        <Field label="5. Бюджет" error={errors["budget"]}>
          <RadioGroup value={form.budget} onChange={(v) => setForm({ ...form, budget: v as any })}
            options={RESIDE_BUDGETS.map((v) => ({ value: v, label: resideBudgetLabels[v] }))} />
        </Field>
        <Field label="6. Когда планируешь покупать" error={errors["timeline"]}>
          <RadioGroup value={form.timeline} onChange={(v) => setForm({ ...form, timeline: v as any })}
            options={RESIDE_TIMELINES.map((v) => ({ value: v, label: resideTimelineLabels[v] }))} />
        </Field>
        <Field label="7. Имя" error={errors["name"]}>
          <input className={`ios-input ${errors["name"] ? "ios-input-error" : ""}`}
            value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Контакт" error={errors["contact"]}>
          <input className={`ios-input ${errors["contact"] ? "ios-input-error" : ""}`}
            value={form.contact ?? ""} onChange={(e) => setForm({ ...form, contact: e.target.value })}
            placeholder="@username или номер" />
        </Field>
        <Field label="Удобный канал связи" error={errors["channel"]}>
          <RadioGroup value={form.channel} onChange={(v) => setForm({ ...form, channel: v as any })}
            options={RESIDE_CHANNELS.map((v) => ({ value: v, label: resideChannelLabels[v] }))} />
        </Field>
        <div className="pt-2">
          <SubmitButton loading={loading}>Получить расчёт доходности</SubmitButton>
        </div>
      </form>
    </div>
  );
}
